import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '@/components/SEO';

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); }
    }, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-section ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function OurPurposePage() {
  return (
    <div data-testid="our-purpose-page" className="bg-[#FAF9F6]">
      <SEO title="Our Purpose" description="We believe great spaces can help people do more. Better practitioners. Stronger businesses. Thriving neighbourhoods." path="/our-purpose" />

      <style>{`
        .reveal-section { opacity: 0; transform: translateY(24px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .reveal-section.revealed { opacity: 1; transform: translateY(0); }
        .editorial-label { font-size: 11px; letter-spacing: 0.25em; text-transform: uppercase; font-weight: 600; color: #8A8B85; }
        .brand-statement { font-family: 'Outfit', sans-serif; font-weight: 500; color: #1A1A1A; line-height: 1.15; }
        .thin-rule { width: 40px; height: 1px; background: #D8DBD3; }
      `}</style>

      {/* ── OUR PURPOSE (combined opening) ── */}
      <section className="pt-24 sm:pt-32 lg:pt-36 pb-14 sm:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="editorial-label mb-6">Our Purpose</p>
            <h1 className="brand-statement text-4xl sm:text-5xl lg:text-[3.5rem] mb-6">
              We believe great spaces can help people do&nbsp;more.
            </h1>
            <p className="text-base sm:text-lg text-[#5C5C55] leading-relaxed max-w-xl mx-auto mb-10">
              Go Freestyle gives practitioners the space to grow their businesses, shape their futures and help their neighbourhoods&nbsp;thrive.
            </p>
            <div className="space-y-1">
              <p className="brand-statement text-2xl sm:text-3xl">Better practitioners.</p>
              <p className="brand-statement text-2xl sm:text-3xl">Stronger businesses.</p>
              <p className="brand-statement text-2xl sm:text-3xl">Thriving neighbourhoods.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── OUR VISION ── */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="editorial-label mb-4">Our Vision</p>
            <div className="thin-rule mx-auto mb-8" />
            <h2 className="brand-statement text-4xl sm:text-5xl lg:text-[3.5rem]">
              To create the best studios in the world for&nbsp;practitioners.
            </h2>
          </Reveal>
        </div>
      </section>

      {/* ── OUR MISSION ── */}
      <section className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="editorial-label mb-4">Our Mission</p>
            <div className="thin-rule mx-auto mb-8" />
            <h2 className="brand-statement text-4xl sm:text-5xl lg:text-[3.5rem] mb-6">
              To empower practitioners to do&nbsp;more.
            </h2>
            <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto">
              Go Freestyle is about giving independent practitioners the space, environment and platform to build businesses on their own terms. Not simply renting a room — empowering a&nbsp;career.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── OUR MOVEMENT ── */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <p className="editorial-label mb-4">Our Movement</p>
            <div className="thin-rule mx-auto mb-8" />
            <div className="space-y-2 mb-8">
              <h2 className="brand-statement text-3xl sm:text-4xl lg:text-5xl">One neighbourhood.</h2>
              <h2 className="brand-statement text-3xl sm:text-4xl lg:text-5xl">One business.</h2>
              <h2 className="brand-statement text-3xl sm:text-4xl lg:text-5xl">One studio at a time.</h2>
            </div>
            <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto">
              Go Freestyle's ambition is to grow through neighbourhoods — creating a network of independent practitioners and businesses while retaining the local character and human connection that makes each community&nbsp;different.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FOUR BRAND ICONS ── */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-8">
          <Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 sm:gap-8">
              {[
                { icon: <SupportingPeopleIcon />, label: 'Supporting', sublabel: 'People' },
                { icon: <BuildingCommunitiesIcon />, label: 'Building', sublabel: 'Communities' },
                { icon: <CreatingOpportunityIcon />, label: 'Creating', sublabel: 'Opportunity' },
                { icon: <InspiringFutureIcon />, label: 'Inspiring', sublabel: 'Brighter Futures' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div className="flex justify-center mb-4">{item.icon}</div>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C55]">{item.label}</p>
                  <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C5C55]">{item.sublabel}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CLOSING CTA ── */}
      <section className="py-20 sm:py-28 lg:py-32 bg-[#3A4035]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white tracking-tight mb-8">
              Your future starts here.
            </h2>
            <Link to="/book-a-viewing" className="inline-flex items-center gap-2 bg-white text-[#3A4035] rounded-full px-8 py-4 text-sm font-medium hover:bg-[#F2F0EB] transition-colors" data-testid="purpose-cta">
              Book a Viewing <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/* ── Thin line icons ── */
function SupportingPeopleIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#3A4035" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8c0 3-2 5-5 5s-5-2-5-5" /><path d="M18 8c0 3 2 5 5 5s5-2 5-5" />
      <circle cx="18" cy="6" r="2" /><path d="M18 8v6" />
      <path d="M12 18c-3 1-5 3-5 6v2h22v-2c0-3-2-5-5-6" />
      <path d="M14 30v-4" /><path d="M22 30v-4" /><path d="M18 28v-8" />
    </svg>
  );
}

function BuildingCommunitiesIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#3A4035" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="10" r="3" /><circle cx="24" cy="10" r="3" /><circle cx="18" cy="20" r="3" />
      <path d="M6 18c0-2 2-4 6-5" /><path d="M30 18c0-2-2-4-6-5" />
      <path d="M12 28c0-2 2-4 6-5" /><path d="M24 28c0-2-2-4-6-5" />
    </svg>
  );
}

function CreatingOpportunityIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#3A4035" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 28V14l12-8 12 8v14" /><path d="M6 28h24" />
      <rect x="14" y="20" width="8" height="8" /><path d="M18 20v8" />
      <path d="M10 18v6" /><path d="M26 18v6" />
    </svg>
  );
}

function InspiringFutureIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="#3A4035" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="18" r="12" />
      <ellipse cx="18" cy="18" rx="5" ry="12" />
      <path d="M6 18h24" /><path d="M8 12h20" /><path d="M8 24h20" />
    </svg>
  );
}
